import type { ChatTag, Lead, WhatsappChat, WhatsappMessage, WhatsappSession } from "../types.ts";

export const CHAT_SIDEBAR_MIN_WIDTH = 300;
export const CHAT_SIDEBAR_MAX_WIDTH = 560;
export const CHAT_SIDEBAR_DEFAULT_WIDTH = 380;

export function filterChats(
  chats: WhatsappChat[],
  filter: string,
  search: string,
  tags: ChatTag[] = [],
): WhatsappChat[] {
  const knownTagIds = new Set(tags.map((tag) => tag.id));
  const normalizedSearch = search.trim().toLowerCase();

  return chats.filter((chat) => {
    const isArchived = !!(chat as { archived?: boolean }).archived;
    // Filtro "Arquivadas" mostra apenas archived; demais filtros escondem.
    if (filter === "archived") {
      if (!isArchived) return false;
    } else if (isArchived) {
      return false;
    }
    if (filter === "unread" && !(chat.unreadCount && chat.unreadCount > 0)) return false;
    if (filter === "groups" && !chat.isGroup) return false;
    if (filter.startsWith("tag:")) {
      const tagId = Number(filter.slice(4));
      if (!knownTagIds.has(tagId)) return false;
      if (!Array.isArray(chat.tagIds) || !chat.tagIds.includes(tagId)) return false;
    }
    if (!normalizedSearch) return true;
    const blob = [
      chat.pushName,
      chat.subject,
      chat.phone,
      chat.lastText,
      displayNameForChat(chat),
    ].map((value) => String(value || "").toLowerCase()).join(" ");
    return blob.includes(normalizedSearch);
  });
}

export function totalUnread(chats: WhatsappChat[]): number {
  return chats.reduce((acc, chat) => acc + (chat.unreadCount || 0), 0);
}

export function totalGroups(chats: WhatsappChat[]): number {
  return chats.filter((chat) => chat.isGroup).length;
}

export function totalArchived(chats: WhatsappChat[]): number {
  return chats.filter((chat) => (chat as { archived?: boolean }).archived).length;
}

export function compareChatsForList(a: WhatsappChat, b: WhatsappChat): number {
  if (!!a.pinnedAt !== !!b.pinnedAt) return a.pinnedAt ? -1 : 1;
  if (a.pinnedAt && b.pinnedAt) return String(b.pinnedAt).localeCompare(String(a.pinnedAt));
  return (b.lastTsMs || 0) - (a.lastTsMs || 0);
}

export function sortChatsForList(chats: WhatsappChat[]): WhatsappChat[] {
  return [...chats].sort(compareChatsForList);
}

function chatAliasJids(chat: Pick<WhatsappChat, "chatJid">): string[] {
  const aliases = (chat as { aliasJids?: unknown }).aliasJids;
  if (!Array.isArray(aliases)) return [];
  return aliases.map((jid) => String(jid || "").trim()).filter(Boolean);
}

function chatIdentityPhone(chat: Pick<WhatsappChat, "chatJid" | "phone" | "providerChatId" | "isGroup">): string {
  if (chat.isGroup) return "";
  return chatPhoneCandidates(chat)[0] || "";
}

function chatIdentityKey(chat: Pick<WhatsappChat, "chatJid" | "phone" | "providerChatId" | "isGroup" | "accountSessionId">): string {
  return chatIdentityKeys(chat)[0] || "";
}

function chatIdentityKeys(chat: Pick<WhatsappChat, "chatJid" | "phone" | "providerChatId" | "isGroup" | "accountSessionId">): string[] {
  const account = String(chat.accountSessionId || "");
  const keys: string[] = [];
  const chatJid = String(chat.chatJid || "").trim();
  if (chatJid) keys.push(`${account}:chat:${chatJid}`);
  const phone = chatIdentityPhone(chat);
  if (phone) keys.push(`${account}:phone:${phone}`);
  return keys;
}

function shouldPreferPrimaryChat(candidate: WhatsappChat, current: WhatsappChat): boolean {
  const candidateIsPhoneJid = !!phoneFromChatJid(candidate.chatJid);
  const currentIsPhoneJid = !!phoneFromChatJid(current.chatJid);
  if (candidateIsPhoneJid !== currentIsPhoneJid) return !candidateIsPhoneJid;
  return false;
}

function preferredChatName(current: unknown, candidate: unknown): string {
  const currentName = String(current || "").trim();
  const candidateName = String(candidate || "").trim();
  if (!candidateName) return currentName;
  if (!currentName) return candidateName;
  if (isGenericContactName(currentName) || isMaskedPhoneName(currentName) || phoneDigitsForDisplay(currentName)) {
    return candidateName;
  }
  return currentName;
}

function preferredPhone(current: unknown, candidate: unknown): string {
  const currentPhone = phoneDigitsForMatching(current);
  const candidatePhone = phoneDigitsForMatching(candidate);
  if (!candidatePhone) return currentPhone;
  if (!currentPhone) return candidatePhone;
  if (candidatePhone.length > currentPhone.length) return candidatePhone;
  return currentPhone;
}

function mergeDuplicateChatRows(current: WhatsappChat, duplicate: WhatsappChat): WhatsappChat {
  const primary = shouldPreferPrimaryChat(duplicate, current) ? duplicate : current;
  const secondary = primary === current ? duplicate : current;
  const latest = (duplicate.lastTsMs || 0) > (current.lastTsMs || 0) ? duplicate : current;
  const avatar = preferredAvatar(primary, secondary);
  const aliases = new Set<string>([
    ...chatAliasJids(current),
    ...chatAliasJids(duplicate),
    current.chatJid,
    duplicate.chatJid,
  ]);
  aliases.delete(primary.chatJid);
  const phone = preferredPhone(
    chatIdentityPhone(primary) || primary.phone,
    chatIdentityPhone(secondary) || secondary.phone,
  );
  const tagIds = new Set<number>([
    ...((current.tagIds || []) as number[]),
    ...((duplicate.tagIds || []) as number[]),
  ]);

  return {
    ...secondary,
    ...primary,
    pushName: preferredChatName(primary.pushName, secondary.pushName),
    phone,
    lastMsgId: latest.lastMsgId || primary.lastMsgId || secondary.lastMsgId || "",
    lastText: latest.lastText || primary.lastText || secondary.lastText || "",
    lastKind: latest.lastKind || primary.lastKind || secondary.lastKind || "",
    lastFromMe: latest.lastFromMe,
    lastStatus: latest.lastStatus ?? primary.lastStatus ?? secondary.lastStatus,
    lastTsMs: latest.lastTsMs || primary.lastTsMs || secondary.lastTsMs || 0,
    unreadCount: (current.unreadCount || 0) + (duplicate.unreadCount || 0),
    pinnedAt: latest.pinnedAt || primary.pinnedAt || secondary.pinnedAt || null,
    tagIds: Array.from(tagIds),
    ...avatar,
    aliasJids: Array.from(aliases),
  };
}

function preferredAvatar(primary: WhatsappChat, secondary: WhatsappChat): Partial<WhatsappChat> {
  const primaryUrl = typeof primary.avatarUrl === "string" && primary.avatarUrl ? primary.avatarUrl : "";
  const secondaryUrl = typeof secondary.avatarUrl === "string" && secondary.avatarUrl ? secondary.avatarUrl : "";
  if (!primaryUrl && !secondaryUrl) return {};
  const primaryFetchedAt = Number(primary.avatarFetchedAt || 0);
  const secondaryFetchedAt = Number(secondary.avatarFetchedAt || 0);
  const selected = secondaryUrl && (!primaryUrl || secondaryFetchedAt >= primaryFetchedAt) ? secondary : primary;
  return {
    avatarUrl: selected.avatarUrl,
    avatarLookupJid: selected.avatarLookupJid,
    avatarFetchedAt: selected.avatarFetchedAt,
  };
}

export function coalesceChatsForList(chats: WhatsappChat[]): WhatsappChat[] {
  const grouped = new Map<string, WhatsappChat>();
  const groupKeys = new Map<string, Set<string>>();
  const keyIndex = new Map<string, string>();
  const passthrough: WhatsappChat[] = [];

  for (const chat of chats) {
    const keys = chatIdentityKeys(chat);
    if (!keys.length) {
      passthrough.push(chat);
      continue;
    }
    const matched = Array.from(new Set(keys.map((key) => keyIndex.get(key)).filter(Boolean) as string[]));
    if (!matched.length) {
      const key = keys[0];
      grouped.set(key, chat);
      groupKeys.set(key, new Set(keys));
      for (const itemKey of keys) keyIndex.set(itemKey, key);
      continue;
    }

    const canonical = matched[0];
    let merged = mergeDuplicateChatRows(grouped.get(canonical) || chat, chat);
    const mergedKeys = groupKeys.get(canonical) || new Set<string>();
    for (const key of keys) mergedKeys.add(key);

    for (const duplicateCanonical of matched.slice(1)) {
      const existing = grouped.get(duplicateCanonical);
      if (existing) merged = mergeDuplicateChatRows(merged, existing);
      const existingKeys = groupKeys.get(duplicateCanonical) || new Set<string>();
      for (const key of existingKeys) mergedKeys.add(key);
      grouped.delete(duplicateCanonical);
      groupKeys.delete(duplicateCanonical);
    }

    for (const key of chatIdentityKeys(merged)) mergedKeys.add(key);
    grouped.set(canonical, merged);
    groupKeys.set(canonical, mergedKeys);
    for (const itemKey of mergedKeys) keyIndex.set(itemKey, canonical);
  }

  return sortChatsForList([...grouped.values(), ...passthrough]);
}

export function aliasJidsForChat(chat: WhatsappChat, chats: WhatsappChat[]): string[] {
  const keys = new Set(chatIdentityKeys(chat));
  const aliases = new Set<string>(chatAliasJids(chat));
  if (keys.size) {
    for (const item of chats) {
      if (item.chatJid === chat.chatJid) continue;
      if (!chatIdentityKeys(item).some((key) => keys.has(key))) continue;
      aliases.add(item.chatJid);
      for (const alias of chatAliasJids(item)) aliases.add(alias);
    }
  }
  aliases.delete(chat.chatJid);
  return Array.from(aliases);
}

export function clampSidebarWidth(width: number): number {
  if (!Number.isFinite(width)) return CHAT_SIDEBAR_DEFAULT_WIDTH;
  return Math.max(CHAT_SIDEBAR_MIN_WIDTH, Math.min(CHAT_SIDEBAR_MAX_WIDTH, Math.round(width)));
}

export function messageStatusForChatPreview(
  chat: Pick<WhatsappChat, "lastFromMe" | "lastMsgId" | "lastStatus">,
  messages: Pick<WhatsappMessage, "messageKey" | "status">[] = [],
): number | undefined {
  if (!chat.lastFromMe) return chat.lastStatus;
  const lastMsgId = String(chat.lastMsgId || "");
  if (!lastMsgId) return chat.lastStatus;
  const message = messages.find((item) => item.messageKey === lastMsgId);
  return typeof message?.status === "number" ? message.status : chat.lastStatus;
}

export function applyMessageStatusToChats(
  chats: WhatsappChat[],
  messageKey: string,
  status: number,
): WhatsappChat[] {
  let changed = false;
  const next = chats.map((chat) => {
    if (chat.lastMsgId !== messageKey) return chat;
    if (chat.lastStatus === status) return chat;
    changed = true;
    return { ...chat, lastStatus: status };
  });
  return changed ? next : chats;
}

export type MessageAckKind = "pending" | "sent" | "delivered" | "read";

export function messageAckKind(status: unknown): MessageAckKind {
  const value = Number(status || 0);
  if (value >= 4) return "read";
  if (value >= 3) return "delivered";
  if (value >= 1) return "sent";
  return "pending";
}

export function persistedOutgoingAckStatus(status: unknown, provider: unknown = ""): number {
  const value = Number(status || 0);
  if (value > 0) return value;
  return String(provider || "").toLowerCase() === "unipile" ? 2 : 1;
}

function phoneDigitsForDisplay(raw: unknown): string {
  const digits = String(raw || "").replace(/\D+/g, "");
  if (digits.length < 8 || digits.length > 15) return "";
  return digits;
}

function phoneDigitsForMatching(raw: unknown): string {
  let digits = String(raw || "").replace(/\D+/g, "");
  if ((digits.length === 10 || digits.length === 11) && !digits.startsWith("55")) {
    digits = `55${digits}`;
  }
  if (digits.length < 8 || digits.length > 15) return "";
  return digits;
}

export function phoneFromChatJid(chatJid: unknown): string {
  const jid = String(chatJid || "").trim();
  const local = jid.split("@")[0].split(":")[0];
  if (!/^\d+$/.test(local)) return "";
  return phoneDigitsForDisplay(local);
}

function phoneFromChatValue(value: unknown): string {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (raw.includes("@")) return phoneDigitsForMatching(phoneFromChatJid(raw));
  return phoneDigitsForMatching(raw);
}

function chatPhoneCandidates(chat: Pick<WhatsappChat, "chatJid" | "phone" | "providerChatId">): string[] {
  return [
    phoneFromChatValue(chat.phone),
    phoneFromChatValue(chat.providerChatId),
    phoneFromChatValue(chat.chatJid),
  ].filter(Boolean);
}

export function avatarLookupJidForChat(chat: Pick<WhatsappChat, "chatJid" | "phone" | "isGroup" | "providerChatId">): string {
  if (!chat.isGroup) {
    const phoneDigits = phoneDigitsForDisplay(chat.phone);
    if (phoneDigits) return `${phoneDigits}@s.whatsapp.net`;

    const providerChatDigits = phoneFromChatJid(chat.providerChatId);
    if (providerChatDigits) return `${providerChatDigits}@s.whatsapp.net`;

    const chatJidDigits = phoneFromChatJid(chat.chatJid);
    if (chatJidDigits) return `${chatJidDigits}@s.whatsapp.net`;
  }
  return String(chat.chatJid || "").trim();
}

export function avatarPatchForChat(
  chat: Pick<WhatsappChat, "chatJid" | "phone" | "isGroup" | "providerChatId">,
  avatarUrl: string | null,
  fetchedAt = Date.now(),
): Partial<WhatsappChat> & { chatJid: string } {
  return {
    chatJid: chat.chatJid,
    avatarUrl,
    avatarLookupJid: avatarLookupJidForChat(chat),
    avatarFetchedAt: fetchedAt,
  };
}

export function shouldRefreshChatsAfterSend(chat: Pick<WhatsappChat, "chatJid" | "provider">): boolean {
  const provider = String(chat.provider || "").toLowerCase();
  if (provider === "unipile") return true;
  const jid = String(chat.chatJid || "");
  return !!jid && !jid.includes("@");
}

export function buildShowLeadPatch(lead: Pick<Lead, "id" | "funnelId"> | null | undefined): {
  panel: "kanban";
  selectedLeadId: string;
  activeFunnelId?: string;
} {
  const selectedLeadId = lead?.id !== undefined && lead?.id !== null ? String(lead.id) : "";
  const activeFunnelId = String(lead?.funnelId || "").trim();
  return activeFunnelId
    ? { panel: "kanban", selectedLeadId, activeFunnelId }
    : { panel: "kanban", selectedLeadId };
}

export function shouldAutoLoadChatHistory(
  chat: Pick<WhatsappChat, "chatJid" | "lastMsgId">,
  loadedMessages: readonly unknown[] = [],
): boolean {
  const chatJid = String(chat.chatJid || "").trim();
  if (!chatJid) return false;
  if (chat.lastMsgId) return true;
  if (loadedMessages.length > 0) return true;
  return !phoneFromChatJid(chatJid);
}

function messageIdentity(message: Pick<WhatsappMessage, "id" | "messageKey" | "chatJid" | "tsMs" | "fromMe" | "text">): string {
  const key = String(message.messageKey || "").trim();
  if (key) return `key:${key}`;
  if (message.id !== undefined && message.id !== null) return `id:${message.id}`;
  return [
    "fallback",
    message.chatJid || "",
    message.tsMs || 0,
    message.fromMe ? "out" : "in",
    message.text || "",
  ].join(":");
}

export function mergeMessagePages(
  loadedMessages: WhatsappMessage[] = [],
  olderMessages: WhatsappMessage[] = [],
): WhatsappMessage[] {
  const byKey = new Map<string, WhatsappMessage>();
  for (const message of [...olderMessages, ...loadedMessages]) {
    const key = messageIdentity(message);
    if (!byKey.has(key) || loadedMessages.includes(message)) {
      byKey.set(key, message);
    }
  }
  return Array.from(byKey.values()).sort((a, b) => (a.tsMs || 0) - (b.tsMs || 0));
}

export function oldestLoadedMessageTs(messages: Pick<WhatsappMessage, "tsMs">[] = []): number {
  return messages.reduce((oldest, message) => {
    const ts = Number(message.tsMs || 0);
    if (!ts) return oldest;
    return oldest ? Math.min(oldest, ts) : ts;
  }, 0);
}

/** Detecta push_names que são "phone mascarado" do WhatsApp/Unipile.
 * Padrões observados:
 *   "+55∙∙∙∙∙∙∙∙11"          (BR com bullets ocultos)
 *   "+14 0 53 13 97 079089"  (phone fake/longo com espaços)
 *   "+5527∙∙∙∙∙∙∙11"
 *   "+55 4 79 65 51 685"     (12 dígitos BR com espaços entre dígitos)
 *   "+17 8 63 63 80 451061"  (qualquer "+digits com espaços")
 * Esses não são nomes reais — devolvemos true pra que displayNameForChat caia
 * no fallback (phone formatado ou ID). */
function isMaskedPhoneName(name: string): boolean {
  const trimmed = name.trim();
  if (!trimmed.startsWith("+")) return false;
  // Bullet/dot characters de mascaramento
  if (/[∙•·]/.test(trimmed)) return true;
  // Qualquer "+digits com pelo menos um espaço entre dígitos" — Unipile sempre
  // separa dígitos pra mascarar quando o phone real está oculto.
  if (/^\+[\d\s]+$/.test(trimmed) && /\s/.test(trimmed)) return true;
  return false;
}

function isGenericContactName(name: string): boolean {
  return name === "Contato" || name === "Contato sem nome";
}

export function displayNameForChat(chat: Pick<WhatsappChat, "chatJid" | "subject" | "pushName" | "phone" | "isGroup">): string {
  const subject = String(chat.subject || "").trim();
  const pushName = String(chat.pushName || "").trim();
  if (subject) return subject;
  // Push_name só vale se não for mascaramento Unipile
  const pushIsMasked = pushName && isMaskedPhoneName(pushName);
  const pushIsGeneric = pushName && isGenericContactName(pushName);
  if (pushName && !pushIsMasked && !pushIsGeneric) return pushName;
  // chat.phone ja aparece como numero no subtitulo; quando nao ha nome real,
  // use o mesmo numero no titulo em vez de "Contato ...1234".
  const phoneDigits = phoneDigitsForDisplay(chat.phone);
  if (phoneDigits) return formatPhone(phoneDigits);
  if (chat.isGroup) {
    const id = String(chat.chatJid || "").split("@")[0].slice(0, 12);
    return id ? `Grupo ${id}` : "Grupo";
  }
  // Push_name mascarado → tenta extrair últimos 4 dígitos pra identificar
  // ("Contato ...7467") em vez de mostrar o monstro inteiro ("+79 8 86 89 54 7467")
  if (pushIsMasked) {
    const digits = pushName.replace(/\D+/g, "");
    const last4 = digits.slice(-4);
    return last4 ? `Contato ...${last4}` : "Contato";
  }
  const jid = String(chat.chatJid || "");
  if (jid.endsWith("@lid")) return "Contato";
  // JID real do WhatsApp é numérico (ex "5511999999999@s.whatsapp.net").
  // IDs base64 da Unipile ("CBT55Z7WXmSnd__6zcKddg") NÃO são phone — quando
  // o attendee não está resolvido (Unipile retorna phone_number="hidden" pra
  // contatos fora da agenda, ou quando a conta está desconectada e a importação
  // veio só do /messages global), o local part é o id interno deles. Mostrar
  // "+CBT55Z7..." confunde o usuário. Cai pro fallback genérico.
  const jidPhoneDigits = phoneFromChatJid(jid);
  if (jidPhoneDigits) return formatPhone(jidPhoneDigits);
  return "Contato";
}

type CentralLeadDisplay = {
  fullName?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  avatarUrl?: string;
};

export function chatPatchFromCentralLead(
  chat: Pick<WhatsappChat, "chatJid" | "subject" | "pushName" | "phone" | "isGroup">,
  lead: CentralLeadDisplay,
): Partial<WhatsappChat> {
  const patch: Partial<WhatsappChat> = {};
  const leadName = String(
    lead.fullName
      || [lead.firstName, lead.lastName].filter(Boolean).join(" ")
      || lead.email
      || "",
  ).trim();
  const subject = String(chat.subject || "").trim();
  const pushName = String(chat.pushName || "").trim();
  const pushNameIsOnlyPhone = !!phoneDigitsForDisplay(pushName);
  const canReplaceName = !subject && (!pushName || isMaskedPhoneName(pushName) || pushNameIsOnlyPhone || isGenericContactName(pushName));
  if (leadName && canReplaceName) {
    patch.pushName = leadName;
  }
  const leadPhone = phoneDigitsForDisplay(lead.phone);
  const chatPhone = phoneDigitsForDisplay(chat.phone);
  if (leadPhone && !chatPhone) {
    patch.phone = leadPhone;
  }
  const avatarUrl = String(lead.avatarUrl || "").trim();
  if (avatarUrl) {
    patch.avatarUrl = avatarUrl;
  }
  return patch;
}

export function findLeadForChat(
  chat: Pick<WhatsappChat, "chatJid" | "phone" | "providerChatId" | "leadId">,
  leads: Lead[] = [],
): Lead | null {
  const leadId = String(chat.leadId || "");
  if (leadId) {
    const byId = leads.find((lead) => String(lead.id) === leadId);
    if (byId) return byId;
  }

  const phones = new Set(chatPhoneCandidates(chat));
  if (!phones.size) return null;
  return leads.find((lead) => {
    const leadPhone = phoneDigitsForMatching(lead.phone);
    return !!leadPhone && phones.has(leadPhone);
  }) || null;
}

export function chatPatchFromKanbanLead(
  chat: Pick<WhatsappChat, "chatJid" | "subject" | "pushName" | "phone" | "isGroup">,
  lead: Lead | null | undefined,
): Partial<WhatsappChat> {
  if (!lead) return {};
  const patch: Partial<WhatsappChat> = {};
  const leadName = String(lead.name || lead.email || "").trim();
  const leadPhone = phoneDigitsForMatching(lead.phone);
  const subject = String(chat.subject || "").trim();
  const pushName = String(chat.pushName || "").trim();
  const currentDisplay = displayNameForChat(chat);
  const leadPhoneDisplay = leadPhone ? formatPhone(leadPhone) : "";
  const canReplaceName = !subject && (
    !pushName
    || isMaskedPhoneName(pushName)
    || isGenericContactName(pushName)
    || (!!leadPhoneDisplay && currentDisplay === leadPhoneDisplay)
  );
  if (leadName && canReplaceName) {
    patch.pushName = leadName;
  }
  if (leadPhone && !phoneDigitsForDisplay(chat.phone)) {
    patch.phone = leadPhone;
  }
  if (lead.id !== undefined && lead.id !== null) {
    patch.leadId = lead.id;
  }
  return patch;
}

export function hydrateChatFromKanbanLead(chat: WhatsappChat, lead: Lead | null | undefined): WhatsappChat {
  if (!lead) return chat;
  return { ...chat, ...chatPatchFromKanbanLead(chat, lead) };
}

function isWhatsappAccount(account: WhatsappSession | null | undefined): account is WhatsappSession {
  return !!account && (!account.accountType || account.accountType === "whatsapp");
}

export function selectWhatsappAccount(
  accounts: WhatsappSession[] = [],
  accountFilter: string[] = [],
  whatsappSession: WhatsappSession | null = null,
): WhatsappSession | null {
  const whatsappAccounts = (accounts || []).filter(isWhatsappAccount);
  const selectedAccountId = (accountFilter || [])[0] || "";
  const selectedAccount = selectedAccountId
    ? whatsappAccounts.find((account) => account.sessionId === selectedAccountId) || null
    : null;
  const unipileAccounts = whatsappAccounts.filter((account) => account.provider === "unipile");
  const selectedUnipileAccount = selectedAccount?.provider === "unipile" ? selectedAccount : null;
  const defaultUnipileAccount = unipileAccounts.find((account) => account.isDefault) || null;
  const sessionIsUnipile = whatsappSession?.provider === "unipile" && isWhatsappAccount(whatsappSession);
  return selectedUnipileAccount
    || defaultUnipileAccount
    || (sessionIsUnipile ? whatsappSession : null)
    || unipileAccounts[0]
    || selectedAccount
    || whatsappAccounts.find((account) => account.isDefault)
    || (isWhatsappAccount(whatsappSession) ? whatsappSession : null)
    || whatsappAccounts[0]
    || null;
}

export function ensureChatAccount(
  chat: WhatsappChat,
  accounts: WhatsappSession[] = [],
  accountFilter: string[] = [],
  whatsappSession: WhatsappSession | null = null,
): WhatsappChat {
  if (chat.accountSessionId) return chat;
  const account = selectWhatsappAccount(accounts, accountFilter, whatsappSession);
  if (!account?.sessionId) return chat;
  return {
    ...chat,
    provider: chat.provider || account.provider || "unipile",
    accountSessionId: account.sessionId,
  };
}

export function chatSubtitleFor(chat: WhatsappChat): string {
  if (chat.isGroup) return "Grupo";
  const phoneDigits = phoneDigitsForDisplay(chat.phone);
  if (phoneDigits) return formatPhone(phoneDigits);
  if (String(chat.chatJid || "").endsWith("@lid")) return "WhatsApp";
  return "Contato";
}

export function formatPhone(raw: unknown): string {
  const digits = String(raw || "").replace(/\D+/g, "");
  if (!digits) return "";
  if (digits.length === 13 && digits.startsWith("55")) {
    return `+55 (${digits.slice(2, 4)}) ${digits.slice(4, 9)}-${digits.slice(9)}`;
  }
  if (digits.length === 12 && digits.startsWith("55")) {
    return `+55 (${digits.slice(2, 4)}) ${digits.slice(4, 8)}-${digits.slice(8)}`;
  }
  return `+${digits}`;
}

export function previewTextForChat(chat: Pick<WhatsappChat, "lastText" | "lastKind">): string {
  if (chat.lastText) return chat.lastText;
  const labels: Record<string, string> = {
    image: "Foto",
    video: "Video",
    gif: "GIF",
    audio: "Audio",
    voice: "Mensagem de voz",
    document: "Documento",
    location: "Localizacao",
    sticker: "Figurinha",
    reaction: "Reacao",
    contact: "Contato",
    poll: "Enquete",
    call: "Chamada",
    unknown: "Mensagem nao suportada",
  };
  return labels[String(chat.lastKind || "")] || "";
}

export function previewKindPrefix(kind: unknown): string {
  const labels: Record<string, string> = {
    image: "[foto] ",
    video: "[video] ",
    gif: "[gif] ",
    voice: "[voz] ",
    audio: "[audio] ",
    document: "[doc] ",
    location: "[local] ",
    sticker: "[sticker] ",
  };
  return labels[String(kind || "")] || "";
}

const AUDIO_FILENAME_RE = /\.(aac|amr|m4a|mp3|oga|ogg|opus|wav|webm)$/i;
const IMAGE_FILENAME_RE = /\.(avif|bmp|gif|heic|heif|jpe?g|png|webp)$/i;
const VIDEO_FILENAME_RE = /\.(3gp|m4v|mov|mp4|mpeg|mpg|webm)$/i;

export function effectiveMessageKind(
  message: Pick<WhatsappMessage, "kind" | "mediaMime" | "mediaFileName" | "mediaFilename">,
): string {
  const kind = String(message.kind || "");
  if (kind === "audio" || kind === "voice") return kind;
  if (kind !== "document") return kind;

  const mime = String(message.mediaMime || "").toLowerCase().split(";")[0].trim();
  if (mime.startsWith("audio/")) return "audio";
  if (mime.startsWith("image/")) return "image";
  if (mime.startsWith("video/")) return "video";

  const filename = String(message.mediaFileName || message.mediaFilename || "").trim();
  if (AUDIO_FILENAME_RE.test(filename)) return "audio";
  if (IMAGE_FILENAME_RE.test(filename)) return "image";
  if (VIDEO_FILENAME_RE.test(filename)) return "video";

  return kind;
}

export function dateLabelFor(ts: unknown, now = new Date()): string {
  const d = new Date(typeof ts === "number" ? ts : String(ts || ""));
  if (Number.isNaN(d.getTime())) return "";
  if (d.toDateString() === now.toDateString()) return "Hoje";
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return "Ontem";
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" });
}

export function hashHue(value: unknown): number {
  const text = String(value || "");
  let hue = 0;
  for (let i = 0; i < text.length; i += 1) hue = (hue * 31 + text.charCodeAt(i)) | 0;
  return Math.abs(hue) % 360;
}

export function senderColorFor(value: unknown): string {
  const palette = ["#1fa855", "#e67e22", "#0287b5", "#ad5cc7", "#c2185b", "#3f51b5", "#0a8d6f", "#d35400"];
  return palette[hashHue(value) % palette.length];
}

export function contrastFor(hex: unknown): string {
  const match = String(hex || "").match(/^#([0-9a-f]{6})$/i);
  if (!match) return "#fff";
  const numberValue = parseInt(match[1], 16);
  const r = (numberValue >> 16) & 255;
  const g = (numberValue >> 8) & 255;
  const b = numberValue & 255;
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.55 ? "#111b21" : "#ffffff";
}

export function fileKindFor(file: File): "image" | "video" | "audio" | "document" {
  const mime = file.type || "";
  if (mime.startsWith("image/")) return "image";
  if (mime.startsWith("video/")) return "video";
  if (mime.startsWith("audio/")) return "audio";
  return "document";
}
